
import { images } from "@/mixins/images";
import {
  ACADEMY_LICENSE_IDS,
  ACADEMY_SUBSCRIPTION_IDS,
  LITE_LICENSE_IDS,
  LITE_SUBSCRIPTION_IDS,
  PKG_FLY,
  PKG_GO,
  PKG_WIN,
  VIP_SUBSCRIPTION_IDS,
} from "functions/src/Subscriptions/packageIds";
import PackageIcon from "@/components/common/PackageIcon.vue";
import { Rank } from "@/store/Ranks/RanksInterfaces";
import {
  BinaryTreeNodeUser,
  UserSubStatus,
} from "functions/src/Users/UsersInterfaces";
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import {
  doGetUserTree,
  CLEAR_PENDING_FLAG,
} from "@/store/UserNetwork/UserNetworkInterfaces";

export default defineComponent({
  name: "binary-node",
  components: { PackageIcon },
  data() {
    return {
      maxLevelsToDisplay: 4,
      profilePic: "",
      random: Math.floor(Math.random() * 1000) + 1,
    };
  },
  props: {
    username: { type: String, default: "" },
    level: { type: Number, default: 1 },
  },
  emits: ["changeTreeRoot"],
  computed: {
    ...mapGetters("ranks", ["rankById"]),
    ...mapGetters("userNetwork", ["getTreeForUser", "isNodePendingUpdate"]),
    NodeColClass(): string {
      // Level 1: Centered, medium width on desktop, full on mobile
      if (this.level == 1) return "col-12 col-md-6 offset-md-3";
      // Level 2: Slightly wider on desktop
      if (this.level == 2) return "col-12 col-md-8 offset-md-2";
      // Level 3: Wider
      if (this.level == 3) return "col-12 col-md-10 offset-md-1";
      // Level 4+: Full width
      return "col-12";
    },
    LeafColClass(): string {
      // Logic for children (next level)
      const nextLevel = this.level + 1;
      if (nextLevel >= 3) return "w-100";
      return "w-50";
    },
    UserAcademyRemainingDays(): number {
      // Los días que quedan salen de la suscripción de la familia que más
      // tarde caduque: con las dos generaciones de doc-id conviviendo, quedarse
      // con la primera pintaría "0 días" a alguien vigente. Y este número es el
      // que decide el color del nodo.
      const ids = this.isGtrLite
        ? LITE_SUBSCRIPTION_IDS
        : ACADEMY_SUBSCRIPTION_IDS;
      return Math.max(
        ...ids.map((id) => this.SubscriptionRemainingDays(id) ?? 0)
      );
    },
    isGtrLite(): boolean {
      if (this.node == undefined) return false;
      const subs = this.node.subscriptions || new Array<UserSubStatus>();

      const hasActive = (ids: readonly string[]) =>
        subs.some((s) => ids.includes(s.subscriptionId) && s.isActive === true);

      // La academia (en cualquiera de sus dos generaciones) gana al Lite,
      // exactamente igual que antes.
      if (hasActive(ACADEMY_SUBSCRIPTION_IDS)) return false;
      return hasActive(LITE_SUBSCRIPTION_IDS);
    },
    UserAcademyStatusClass() {
      if (this.UserAcademyRemainingDays <= 0) return "user-inactive";
      else if (this.UserAcademyRemainingDays < 5) return "user-danger";
      else if (this.UserAcademyRemainingDays < 10) return "user-warning";
      else if (this.isGtrLite) return "user-lite-active";
      else return "user-active";
    },
    UserDistributorRemainingDays(): number {
      // EL EMPAREJAMIENTO SE QUEDA (un Lite mira la suya, la academia la suya) y
      // sólo se AÑADE la global: manda la que más días deje. Con sólo la vieja
      // presente el número es exactamente el de antes.
      const family = this.isGtrLite ? LITE_LICENSE_IDS : ACADEMY_LICENSE_IDS;
      return Math.max(
        ...family.map((id) => this.SubscriptionRemainingDays(id))
      );
    },
    UserDistributorStatusClass() {
      if (this.UserDistributorRemainingDays <= 0) return "distributor-inactive";
      else if (this.UserDistributorRemainingDays < 10)
        return "distributor-danger";
      else if (this.UserDistributorRemainingDays < 20)
        return "distributor-warning";
      else return "distributor-active";
    },
    UserRank(): Rank {
      return this.rankById(this.node.rank);
    },
    node(): BinaryTreeNodeUser {
      const n = this.getTreeForUser(this.username) as BinaryTreeNodeUser;
      if (n.pictureUrl != undefined && n.pictureUrl != "") {
        this.updateProfilePicture(n.pictureUrl);
      }
      return this.getTreeForUser(this.username) as BinaryTreeNodeUser;
    },
    isVIP(): boolean {
      if (this.node == undefined || this.node?.subscriptions == undefined)
        return false;
      const isVIP = this.node.subscriptions.some((s) => {
        return s.subscriptionId == "mmi-colombia";
      });
      return isVIP;
    },
    /**
     * El NIVEL del socio, para que `PackageIcon` elija el dibujo y el color.
     *
     * Lo que este nodo sabe del usuario es su lista de suscripciones, no su
     * nivel: hay que deducirlo, y en el mismo orden en que manda el negocio.
     *
     *   · WIN gana a todo, porque quien tiene el documento de WIN tiene además
     *     el de academia y si se mirara primero la academia saldría siempre FLY.
     *     ⚠️ `VIP_SUBSCRIPTION_IDS` es la familia de WIN (`gtr-vip` → `gtr-win`),
     *     NO tiene nada que ver con el `isVIP` de más abajo en este mismo
     *     fichero, que mira `mmi-colombia` — el Acelerador de Riqueza (CMMI),
     *     que no es un escalón de la escalera sino un producto aparte.
     *   · La academia gana al Lite — es la misma precedencia que ya aplica
     *     `isGtrLite` para los días y para el color del nodo.
     *   · Sin ninguno de los tres se cae a FLY, que es el reparto de siempre.
     *
     * Antes esto devolvía DOS iconos para TRES paquetes: FLY y WIN compartían el
     * birrete, así que el árbol no distinguía al socio de arriba del de en
     * medio. Ahora, si el documento de WIN está, se ve el diamante; y si no está
     * —un WIN antiguo al que nunca se le escribió— se sigue viendo lo mismo que
     * hasta hoy, así que no hay forma de que esto empeore ninguna tarjeta.
     */
    UserPackageVisual(): string {
      if (this.node == undefined) return PKG_FLY;
      const subs = this.node.subscriptions || new Array<UserSubStatus>();
      const hasActive = (ids: readonly string[]) =>
        subs.some((s) => ids.includes(s.subscriptionId) && s.isActive === true);

      if (hasActive(VIP_SUBSCRIPTION_IDS)) return PKG_WIN;
      const base = this.isGtrLite ? PKG_GO : PKG_FLY;
      // EL ACELERADOR SUBE DE FLY A WIN, PERO NO SUBE A UN GO — la misma regla
      // que `OrgTreeNode.vue`, donde está razonada. En dos sitios porque los dos
      // árboles pintan el mismo nodo con motores distintos, y que difieran es
      // exactamente cómo se llega a que el mismo socio salga de un paquete en
      // una pantalla y de otro en la de al lado.
      if (base === PKG_FLY && hasActive(["mmi-colombia"])) return PKG_WIN;
      return base;
    },
  },
  methods: {
    SubscriptionRemainingDays(subscriptionId: string): number {
      if (this.node != undefined) {
        const subs = this.node.subscriptions || new Array<UserSubStatus>();
        const academySubs = subs.find((s) => {
          return s.subscriptionId == subscriptionId;
        });
        if (
          academySubs == undefined ||
          academySubs.remainingDays == undefined ||
          academySubs.remainingDays <= 0
        ) {
          return 0;
        } else {
          return academySubs.remainingDays;
        }
      }
      return 0;
    },
    changeRootUserFunc(newUsername: string) {
      this.$emit("changeTreeRoot", newUsername);
    },
    updateProfilePicture(url: string) {
      this.getProfilePicture(url).then((response) => {
        this.profilePic = response;
      });
    },
    nodeId(): string {
      return this.node.username.replace(/[^a-zA-Z0-9]/g, "") + this.random;
    },
    getChildUsername(child: BinaryTreeNodeUser | string | null): string {
      if (child === null || child === undefined) return "";
      if (typeof child === "string") return child;
      return child.username;
    },
    async checkAndUpdateIfPending() {
      if (this.username && this.isNodePendingUpdate(this.username)) {
        try {
          await this.$store.dispatch("userNetwork/" + doGetUserTree, {
            username: this.username,
            levels: 1,
          });
          this.$store.commit(
            "userNetwork/" + CLEAR_PENDING_FLAG,
            this.username
          );
        } catch (error) {
          // Prevent a single node update failure from breaking component mounting.
          // eslint-disable-next-line no-console
          console.error(
            "Failed to update user tree for node",
            this.username,
            error
          );
        }
      }
    },
  },
  async mounted() {
    await this.checkAndUpdateIfPending();
  },
  setup() {
    const getProfilePicture = images.methods.getProfilePicture;
    return {
      getProfilePicture,
    };
  },
});
